import React, { useState } from "react";
import styled from "styled-components";
import { InfoCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { ReactComponent as CalendarTimeIcon } from "../../../assets/svg/Calendar_Time.svg";
import { ReactComponent as NoteIcon } from "../../../assets/svg/Notes.svg";
import { Tooltip, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { GRAY_100, GRAY_200, GRAY_400, PRIMARY_600, WHITE } from "../../../constant/colors";
import { sizes, fontWeights } from "../../../constant/styles";
import { ReactComponent as FileAttachmentIcon } from "../../../assets/svg/attachment.svg";

const { Text } = Typography;
import { downloadAttachment } from "../../../utils/downloadUtils";
import { dateFormatter } from "../../../utils/date";
import AttachmentPermissionsPopover from "../../AttachmentPermissionsPopover";

// Common icon style for consent details
const iconStyle = {
  fontSize: sizes.small,
  color: GRAY_400
};

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  gap: 5px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const DetailItemStretched = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;
  gap: 5px;
`;

const DetailIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
`;

const DetailText = styled.div`
  font-size: ${sizes.small};
  line-height: 1.5em;
`;

const PDFContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 5px;
  background-color: ${GRAY_100};
  border-radius: 5px;
  cursor: ${(props) => (props.isClickable ? "pointer" : "default")};
  transition: background-color 0.2s ease;
  max-width: 100%;
  min-width: 0;

  &:hover {
    background-color: ${(props) => (props.isClickable ? GRAY_200 : GRAY_100)};
  }
`;

const PDFText = styled(DetailText)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

/**
 * Details component for consent action events (received/revoked)
 * Shows formatted action message, notes, and file attachment
 * @param {string} actionTemplate - Formatted template message describing the action (e.g., "Received by John Doe via phone")
 * @param {string} note - Additional notes about the consent
 * @param {Object} attachmentDetails - Attachment object (can be PDF, PNG, DOC, etc.)
 * @param {string} attachmentDetails.fileName - Display name of the file
 * @param {number} attachmentDetails.downloadId - Attachment ID for downloading
 * @param {string} attachmentDetails.attachmentUniqueId - Unique attachment identifier
 * @param {boolean} canViewOrDownloadAttachments - Permission flag from API (ACL-based)
 */
const ConsentActionDetails = ({
  actionTemplate,
  note,
  attachmentDetails,
  effectiveDate,
  canViewOrDownloadAttachments = false
}) => {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);

  // Use the canViewOrDownload permission from API response (ACL-based)
  // Backend checks user role via AttachmentService.canUserViewOrDownload
  const canViewOrDownload = canViewOrDownloadAttachments && !!attachmentDetails?.downloadId;

  const handleFileDownload = (attachmentInfo) => {
    if (!attachmentInfo?.downloadId) {
      return;
    }

    const attachment = {
      id: attachmentInfo.downloadId,
      attachmentUniqueId: attachmentInfo.attachmentUniqueId,
      filename: attachmentInfo.fileName
    };

    downloadAttachment(attachment, setIsDownloading);
  };

  return (
    <DetailsContainer>
      {effectiveDate && (
        <DetailItem>
          <DetailIcon>
            <CalendarTimeIcon />
          </DetailIcon>
          <DetailText>
            {t("consent.effective_date")}: {dateFormatter(effectiveDate, "MM/DD/YYYY")}
          </DetailText>
          <Tooltip
            title={<Text>{t("consent.tooltips.effective_date")}</Text>}
            placement="top"
            color={WHITE}
            overlayInnerStyle={{
              padding: "8px 12px",
              fontSize: sizes.small,
              fontWeight: fontWeights.semibold,
              textAlign: "center"
            }}
          >
            <DetailIcon style={{ cursor: "pointer" }}>
              <InfoCircleOutlined style={{ fontSize: sizes.small, color: GRAY_400 }} />
            </DetailIcon>
          </Tooltip>
        </DetailItem>
      )}
      {actionTemplate && (
        <DetailItem>
          <DetailIcon>
            <InfoCircleOutlined style={iconStyle} />
          </DetailIcon>
          <DetailText>{actionTemplate}</DetailText>
        </DetailItem>
      )}
      {note && (
        <DetailItemStretched>
          <DetailIcon>
            <NoteIcon />
          </DetailIcon>
          <DetailText style={{ flex: 1 }}>{note}</DetailText>
        </DetailItemStretched>
      )}
      {attachmentDetails?.fileName && (
        <DetailItem>
          <AttachmentPermissionsPopover canViewOrDownload={canViewOrDownload}>
            <Tooltip
              title={
                attachmentDetails.downloadId && canViewOrDownload && !isDownloading ? (
                  <Text>{t("consent.download.download_file")}</Text>
                ) : null
              }
              placement="top"
              color={WHITE}
              overlayInnerStyle={{
                padding: "8px 12px",
                fontSize: sizes.small,
                fontWeight: fontWeights.semibold,
                textAlign: "center"
              }}
            >
              <PDFContainer
                isClickable={!!attachmentDetails.downloadId && !isDownloading && canViewOrDownload}
                onClick={() =>
                  attachmentDetails.downloadId &&
                  !isDownloading &&
                  canViewOrDownload &&
                  handleFileDownload(attachmentDetails)
                }
                title={
                  isDownloading
                    ? t("consent.download.downloading")
                    : attachmentDetails.downloadId
                    ? t("consent.download.click_to_download", {
                        fileName: attachmentDetails.fileName
                      })
                    : attachmentDetails.fileName
                }
              >
                <DetailIcon>
                  <FileAttachmentIcon />
                </DetailIcon>
                <PDFText>{attachmentDetails.fileName}</PDFText>
                {isDownloading && (
                  <LoadingOutlined spin style={{ fontSize: "16px", color: PRIMARY_600, marginLeft: "5px" }} />
                )}
              </PDFContainer>
            </Tooltip>
          </AttachmentPermissionsPopover>
        </DetailItem>
      )}
    </DetailsContainer>
  );
};

export default ConsentActionDetails;
